import React, { useMemo, useState } from 'react';
import axios from 'axios';
import {
  Eye,
  File,
  FilePdf,
  FileText,
  FilmStrip,
  Image,
  Lock,
  Trash,
  UploadSimple,
  X,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const API = '/api';

const TECHNICAL_CATEGORIES = [
  ['floor_plan', 'Planta baixa'],
  ['implantation', 'Implantação'],
  ['descriptive_memorial', 'Memorial descritivo'],
  ['permit', 'Alvará'],
  ['license', 'Licença'],
  ['registry', 'Registro ou matrícula'],
  ['schedule', 'Cronograma'],
  ['technical_table', 'Tabela técnica'],
  ['other', 'Outro'],
];

const MAX_OFFICIAL_BYTES = 100 * 1024 * 1024;
const MAX_REFERENCE_BYTES = 15 * 1024 * 1024;

const acceptedOfficialExtensions = [
  '.pdf',
  '.jpg',
  '.jpeg',
  '.png',
  '.mp4',
];

const acceptedReferenceExtensions = [
  '.jpg',
  '.jpeg',
  '.png',
];

const fileExtension = (name) => {
  const normalized = String(name || '').toLowerCase();
  const index = normalized.lastIndexOf('.');
  return index >= 0 ? normalized.slice(index) : '';
};

const fileSizeLabel = (bytes) => {
  if (!bytes) return '';

  const megabytes = bytes / 1024 / 1024;

  if (megabytes < 1) {
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  return `${megabytes.toFixed(2)} MB`;
};

const isImage = (item) =>
  String(item?.mime_type || '').startsWith('image/');

const isVideo = (item) =>
  String(item?.mime_type || '').startsWith('video/');

const isPdf = (item) =>
  item?.mime_type === 'application/pdf' ||
  fileExtension(item?.original_file_name) === '.pdf';

const categoryLabel = (value) =>
  TECHNICAL_CATEGORIES.find(
    ([key]) => key === value
  )?.[1] || 'Outro';

const assetViewUrl = (
  enterpriseId,
  asset,
  section
) => {
  if (!enterpriseId || !asset?.id) return '';

  if (section === 'technical') {
    return `${API}/enterprises/${enterpriseId}/technical-materials/${asset.id}/view`;
  }

  if (section === 'reference') {
    return `${API}/enterprises/${enterpriseId}/reference-images/${asset.id}/view`;
  }

  return `${API}/enterprises/${enterpriseId}/materials/${asset.id}/view`;
};

const EnterpriseAssetsPanel = ({
  enterpriseId,
  form,
  setForm,
  refreshEnterprises,
}) => {
  const [officialFiles, setOfficialFiles] = useState([]);
  const [referenceFiles, setReferenceFiles] = useState([]);
  const [technicalFiles, setTechnicalFiles] = useState([]);

  const [officialName, setOfficialName] = useState('');
  const [referenceName, setReferenceName] = useState('');

  const [technicalName, setTechnicalName] = useState('');
  const [technicalCategory, setTechnicalCategory] =
    useState('floor_plan');
  const [technicalDescription, setTechnicalDescription] =
    useState('');
  const [technicalAiCanSend, setTechnicalAiCanSend] =
    useState(false);

  const [uploadingOfficial, setUploadingOfficial] =
    useState(false);
  const [uploadingReference, setUploadingReference] =
    useState(false);
  const [uploadingTechnical, setUploadingTechnical] =
    useState(false);

  const [preview, setPreview] = useState(null);
  const officialAssets = useMemo(
    () =>
      (form.materials || []).filter(
        (item) => item.file_url
      ),
    [form.materials]
  );

  const technicalAssets =
    form.technical_materials || [];

  const referenceAssets =
    form.reference_images || [];

  const updateFormAssets = (field, values) => {
    setForm((current) => ({
      ...current,
      [field]: values || [],
    }));
  };

  const refreshAll = async () => {
    if (refreshEnterprises) {
      await refreshEnterprises();
    }
  };

  const validateFiles = (
    files,
    extensions,
    maximumBytes,
    typeLabel
  ) => {
    for (const file of files) {
      const extension = fileExtension(file.name);

      if (!extensions.includes(extension)) {
        toast.error(
          `${file.name}: formato não permitido para ${typeLabel}`
        );
        return false;
      }

      if (file.size > maximumBytes) {
        toast.error(
          `${file.name}: arquivo acima do limite de ${
            maximumBytes / 1024 / 1024
          } MB`
        );
        return false;
      }
    }

    return true;
  };

  const uploadSequentially = async ({
    files,
    createPayload,
    endpoint,
    responseField,
    formField,
    setUploading,
    clear,
    successMessage,
  }) => {
    if (!enterpriseId) {
      toast.error(
        'Salve o empreendimento antes de enviar arquivos'
      );
      return;
    }

    if (!files.length) {
      toast.error('Selecione ao menos um arquivo');
      return;
    }

    setUploading(true);

    let latestValues = null;
    let uploadedCount = 0;

    try {
      for (let index = 0; index < files.length; index += 1) {
        const file = files[index];
        const payload = createPayload(file, index);

        const { data } = await axios.post(
          endpoint,
          payload,
          {
            withCredentials: true,
          }
        );

        latestValues = data?.[responseField] || latestValues;
        uploadedCount += 1;
      }

      if (latestValues) {
        updateFormAssets(formField, latestValues);
      }

      clear();

      toast.success(
        uploadedCount === 1
          ? successMessage
          : `${uploadedCount} arquivos enviados com sucesso`
      );

      await refreshAll();
    } catch (error) {
      console.error(error);

      const detail = error?.response?.data?.detail;

      toast.error(
        typeof detail === 'string'
          ? detail
          : `Falha após enviar ${uploadedCount} de ${files.length} arquivo(s)`
      );
    } finally {
      setUploading(false);
    }
  };

  const uploadOfficial = async () => {
    if (
      !validateFiles(
        officialFiles,
        acceptedOfficialExtensions,
        MAX_OFFICIAL_BYTES,
        'a Biblioteca Oficial'
      )
    ) {
      return;
    }

    await uploadSequentially({
      files: officialFiles,
      endpoint: `${API}/enterprises/${enterpriseId}/materials/upload`,
      responseField: 'materials',
      formField: 'materials',
      setUploading: setUploadingOfficial,
      successMessage: 'Material oficial enviado',
      createPayload: (file) => {
        const payload = new FormData();

        payload.append('file', file);
        payload.append(
          'name',
          officialFiles.length === 1 &&
            officialName.trim()
            ? officialName.trim()
            : file.name
        );

        return payload;
      },
      clear: () => {
        setOfficialFiles([]);
        setOfficialName('');

        const input = document.getElementById(
          'official-library-files'
        );

        if (input) input.value = '';
      },
    });
  };

  const uploadReference = async () => {
    if (
      !validateFiles(
        referenceFiles,
        acceptedReferenceExtensions,
        MAX_REFERENCE_BYTES,
        'as Imagens de Referência'
      )
    ) {
      return;
    }

    await uploadSequentially({
      files: referenceFiles,
      endpoint: `${API}/enterprises/${enterpriseId}/reference-images/upload`,
      responseField: 'reference_images',
      formField: 'reference_images',
      setUploading: setUploadingReference,
      successMessage: 'Imagem de referência enviada',
      createPayload: (file) => {
        const payload = new FormData();

        payload.append('file', file);
        payload.append(
          'name',
          referenceFiles.length === 1 &&
            referenceName.trim()
            ? referenceName.trim()
            : file.name
        );

        return payload;
      },
      clear: () => {
        setReferenceFiles([]);
        setReferenceName('');

        const input = document.getElementById(
          'reference-library-files'
        );

        if (input) input.value = '';
      },
    });
  };

  const uploadTechnical = async () => {
    if (
      !validateFiles(
        technicalFiles,
        acceptedOfficialExtensions,
        MAX_OFFICIAL_BYTES,
        'os Materiais Técnicos'
      )
    ) {
      return;
    }

    await uploadSequentially({
      files: technicalFiles,
      endpoint: `${API}/enterprises/${enterpriseId}/technical-materials/upload`,
      responseField: 'technical_materials',
      formField: 'technical_materials',
      setUploading: setUploadingTechnical,
      successMessage: 'Material técnico enviado',
      createPayload: (file) => {
        const payload = new FormData();

        payload.append('file', file);
        payload.append(
          'name',
          technicalFiles.length === 1 &&
            technicalName.trim()
            ? technicalName.trim()
            : file.name
        );
        payload.append('category', technicalCategory);
        payload.append(
          'description',
          technicalDescription.trim()
        );
        payload.append(
          'ai_can_send',
          String(technicalAiCanSend)
        );

        return payload;
      },
      clear: () => {
        setTechnicalFiles([]);
        setTechnicalName('');
        setTechnicalDescription('');
        setTechnicalAiCanSend(false);

        const input = document.getElementById(
          'technical-library-files'
        );

        if (input) input.value = '';
      },
    });
  };

  const deleteAsset = async (
    asset,
    section
  ) => {
    if (!enterpriseId) return;

    const label =
      asset.name ||
      asset.original_file_name ||
      'arquivo';

    if (
      !window.confirm(
        `Excluir "${label}"?`
      )
    ) {
      return;
    }

    const endpoint =
      section === 'technical'
        ? `${API}/enterprises/${enterpriseId}/technical-materials/${asset.id}`
        : section === 'reference'
          ? `${API}/enterprises/${enterpriseId}/reference-images/${asset.id}`
          : `${API}/enterprises/${enterpriseId}/materials/${asset.id}`;

    const responseField =
      section === 'technical'
        ? 'technical_materials'
        : section === 'reference'
          ? 'reference_images'
          : 'materials';

    const formField = responseField;

    try {
      const { data } = await axios.delete(
        endpoint,
        {
          withCredentials: true,
        }
      );

      updateFormAssets(
        formField,
        data?.[responseField] || []
      );

      toast.success('Arquivo excluído');

      if (
        preview?.asset?.id === asset.id
      ) {
        setPreview(null);
      }

      await refreshAll();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Erro ao excluir arquivo'
      );
    }
  };

  const updateTechnicalPermission = async (
    asset,
    checked
  ) => {
    try {
      const { data } = await axios.patch(
        `${API}/enterprises/${enterpriseId}/technical-materials/${asset.id}`,
        {
          ai_can_send: checked,
        },
        {
          withCredentials: true,
        }
      );

      updateFormAssets(
        'technical_materials',
        data?.technical_materials || []
      );

      toast.success(
        checked
          ? 'Envio pela IA autorizado'
          : 'Envio pela IA bloqueado'
      );

      await refreshAll();
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          'Erro ao atualizar permissão'
      );
    }
  };

  return (
    <div className="space-y-8">
      <AssetSection
        title="Biblioteca Oficial"
        subtitle="Primeira fonte de materiais da IA: books, fotos, imagens da obra, folders e vídeos."
        accent="indigo"
        disabled={!enterpriseId}
        disabledText="Salve o empreendimento antes de enviar arquivos."
        uploadArea={
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Field label="Nome do material">
                <input
                  value={officialName}
                  onChange={(event) =>
                    setOfficialName(event.target.value)
                  }
                  disabled={officialFiles.length > 1}
                  className="input-default disabled:bg-slate-100"
                  placeholder={
                    officialFiles.length > 1
                      ? 'No envio múltiplo será usado o nome de cada arquivo'
                      : 'Ex.: Book digital'
                  }
                />
              </Field>

              <Field label="Arquivos">
                <input
                  id="official-library-files"
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.mp4,application/pdf,image/jpeg,image/png,video/mp4"
                  onChange={(event) =>
                    setOfficialFiles(
                      Array.from(
                        event.target.files || []
                      )
                    )
                  }
                  className="input-default"
                />
              </Field>
            </div>

            <UploadFooter
              count={officialFiles.length}
              uploading={uploadingOfficial}
              onUpload={uploadOfficial}
              buttonText="Enviar para Biblioteca"
            />
          </>
        }
      >
        <AssetGrid
          assets={officialAssets}
          enterpriseId={enterpriseId}
          section="official"
          onPreview={setPreview}
          onDelete={deleteAsset}
        />
      </AssetSection>

      <AssetSection
        title="Materiais Técnicos"
        subtitle="Documentos técnicos consultados pela IA. O gestor decide se cada arquivo pode ser enviado automaticamente."
        accent="amber"
        disabled={!enterpriseId}
        disabledText="Salve o empreendimento antes de enviar materiais técnicos."
        uploadArea={
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Field label="Nome do material">
                <input
                  value={technicalName}
                  onChange={(event) =>
                    setTechnicalName(event.target.value)
                  }
                  disabled={technicalFiles.length > 1}
                  className="input-default disabled:bg-slate-100"
                  placeholder="Ex.: Planta baixa — 2 quartos"
                />
              </Field>

              <Field label="Categoria">
                <select
                  value={technicalCategory}
                  onChange={(event) =>
                    setTechnicalCategory(
                      event.target.value
                    )
                  }
                  className="input-default"
                >
                  {TECHNICAL_CATEGORIES.map(
                    ([value, label]) => (
                      <option
                        key={value}
                        value={value}
                      >
                        {label}
                      </option>
                    )
                  )}
                </select>
              </Field>

              <Field label="Arquivos">
                <input
                  id="technical-library-files"
                  type="file"
                  multiple
                  accept=".pdf,.jpg,.jpeg,.png,.mp4,application/pdf,image/jpeg,image/png,video/mp4"
                  onChange={(event) =>
                    setTechnicalFiles(
                      Array.from(
                        event.target.files || []
                      )
                    )
                  }
                  className="input-default"
                />
              </Field>

              <Field label="Observação de uso">
                <input
                  value={technicalDescription}
                  onChange={(event) =>
                    setTechnicalDescription(
                      event.target.value
                    )
                  }
                  className="input-default"
                  placeholder="Opcional"
                />
              </Field>
            </div>

            <label className="mt-4 flex items-center justify-between gap-4 rounded-xl border border-amber-200 bg-white p-4">
              <div>
                <p className="font-semibold text-slate-900">
                  A IA pode enviar este material
                </p>
                <p className="text-sm text-slate-500 mt-1">
                  Quando desativado, a IA registra a solicitação e informa que o especialista dará continuidade.
                </p>
              </div>

              <input
                type="checkbox"
                checked={technicalAiCanSend}
                onChange={(event) =>
                  setTechnicalAiCanSend(
                    event.target.checked
                  )
                }
                className="w-5 h-5"
              />
            </label>

            <UploadFooter
              count={technicalFiles.length}
              uploading={uploadingTechnical}
              onUpload={uploadTechnical}
              buttonText="Enviar Material Técnico"
            />
          </>
        }
      >
        <AssetGrid
          assets={technicalAssets}
          enterpriseId={enterpriseId}
          section="technical"
          onPreview={setPreview}
          onDelete={deleteAsset}
          onPermissionChange={
            updateTechnicalPermission
          }
        />
      </AssetSection>

      <AssetSection
        title="Imagens de Referência"
        subtitle="Fallback utilizado somente quando não houver fotos oficiais. A IA informará ao lead que são referências visuais."
        accent="slate"
        disabled={!enterpriseId}
        disabledText="Salve o empreendimento antes de enviar imagens."
        uploadArea={
          <>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Field label="Nome da imagem">
                <input
                  value={referenceName}
                  onChange={(event) =>
                    setReferenceName(
                      event.target.value
                    )
                  }
                  disabled={referenceFiles.length > 1}
                  className="input-default disabled:bg-slate-100"
                  placeholder="Ex.: Fachada de produto semelhante"
                />
              </Field>

              <Field label="Imagens">
                <input
                  id="reference-library-files"
                  type="file"
                  multiple
                  accept=".jpg,.jpeg,.png,image/jpeg,image/png"
                  onChange={(event) =>
                    setReferenceFiles(
                      Array.from(
                        event.target.files || []
                      )
                    )
                  }
                  className="input-default"
                />
              </Field>
            </div>

            <UploadFooter
              count={referenceFiles.length}
              uploading={uploadingReference}
              onUpload={uploadReference}
              buttonText="Enviar Imagens"
            />
          </>
        }
      >
        <AssetGrid
          assets={referenceAssets}
          enterpriseId={enterpriseId}
          section="reference"
          onPreview={setPreview}
          onDelete={deleteAsset}
        />
      </AssetSection>

      {preview && (
        <PreviewModal
          preview={preview}
          enterpriseId={enterpriseId}
          onClose={() => setPreview(null)}
        />
      )}
    </div>
  );
};

const AssetSection = ({
  title,
  subtitle,
  uploadArea,
  children,
  disabled,
  disabledText,
  accent,
}) => {
  const accentClasses = {
    indigo:
      'border-indigo-200 bg-indigo-50 text-indigo-950',
    amber:
      'border-amber-200 bg-amber-50 text-amber-950',
    slate:
      'border-slate-200 bg-slate-50 text-slate-950',
  };

  return (
    <section
      className={`rounded-xl border p-5 ${
        accentClasses[accent] ||
        accentClasses.slate
      }`}
    >
      <div>
        <h3 className="font-bold">{title}</h3>
        <p className="text-sm opacity-75 mt-1">
          {subtitle}
        </p>
      </div>

      <div className="mt-5">
        {disabled ? (
          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-800">
            {disabledText}
          </div>
        ) : (
          uploadArea
        )}
      </div>

      <div className="mt-6">{children}</div>
    </section>
  );
};

const UploadFooter = ({
  count,
  uploading,
  onUpload,
  buttonText,
}) => (
  <div className="mt-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
    <p className="text-sm text-slate-600">
      {count
        ? `${count} arquivo(s) selecionado(s)`
        : 'Nenhum arquivo selecionado'}
    </p>

    <button
      type="button"
      disabled={uploading || count === 0}
      onClick={onUpload}
      className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-slate-900 text-white font-semibold disabled:opacity-50"
    >
      <UploadSimple size={20} />
      {uploading ? 'Enviando...' : buttonText}
    </button>
  </div>
);

const AssetGrid = ({
  assets,
  enterpriseId,
  section,
  onPreview,
  onDelete,
  onPermissionChange,
}) => {
  if (!assets.length) {
    return (
      <p className="text-sm text-slate-500">
        Nenhum arquivo cadastrado.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
      {assets.map((asset) => {
        const viewUrl = assetViewUrl(
          enterpriseId,
          asset,
          section
        );

        return (
          <article
            key={asset.id}
            className="rounded-xl border border-slate-200 bg-white overflow-hidden"
          >
            <button
              type="button"
              onClick={() =>
                onPreview({
                  asset,
                  section,
                })
              }
              className="block w-full text-left"
            >
              <div className="h-44 bg-slate-100 flex items-center justify-center overflow-hidden">
                {isImage(asset) ? (
                  <img
                    src={viewUrl}
                    alt={
                      asset.name ||
                      asset.original_file_name
                    }
                    className="w-full h-full object-cover"
                  />
                ) : isVideo(asset) ? (
                  <FilmStrip
                    size={48}
                    className="text-slate-500"
                  />
                ) : isPdf(asset) ? (
                  <FilePdf
                    size={48}
                    className="text-red-500"
                  />
                ) : (
                  <File
                    size={48}
                    className="text-slate-500"
                  />
                )}
              </div>
            </button>

            <div className="p-4">
              <p className="font-semibold text-sm text-slate-900 truncate">
                {asset.name ||
                  asset.original_file_name}
              </p>

              <p className="text-xs text-slate-500 mt-1 truncate">
                {section === 'technical'
                  ? categoryLabel(asset.category)
                  : asset.original_file_name}
                {asset.file_size
                  ? ` • ${fileSizeLabel(
                      asset.file_size
                    )}`
                  : ''}
              </p>

              {section === 'technical' && (
                <label className="mt-3 flex items-center justify-between gap-3 rounded-lg bg-slate-50 border border-slate-200 p-3">
                  <span className="text-xs font-semibold text-slate-700">
                    IA pode enviar
                  </span>

                  <input
                    type="checkbox"
                    checked={Boolean(
                      asset.ai_can_send
                    )}
                    onChange={(event) =>
                      onPermissionChange(
                        asset,
                        event.target.checked
                      )
                    }
                    className="w-4 h-4"
                  />
                </label>
              )}

              <div className="mt-4 flex items-center gap-2">
                <button
                  type="button"
                  onClick={() =>
                    onPreview({
                      asset,
                      section,
                    })
                  }
                  className="flex-1 inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg border border-slate-300 text-sm font-semibold"
                >
                  <Eye size={17} />
                  Visualizar
                </button>

                <button
                  type="button"
                  onClick={() =>
                    onDelete(asset, section)
                  }
                  className="p-2 rounded-lg border border-red-200 text-red-600"
                  title="Excluir arquivo"
                >
                  <Trash size={18} />
                </button>
              </div>
            </div>
          </article>
        );
      })}
    </div>
  );
};

const PreviewModal = ({
  preview,
  enterpriseId,
  onClose,
}) => {
  const { asset, section } = preview;

  const viewUrl = assetViewUrl(
    enterpriseId,
    asset,
    section
  );

  return (
    <div className="fixed inset-0 z-[100] bg-slate-950/70 p-4 flex items-center justify-center">
      <div className="w-full max-w-5xl max-h-[94vh] bg-white rounded-2xl overflow-hidden shadow-2xl flex flex-col">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="font-bold text-slate-900 truncate">
              {asset.name ||
                asset.original_file_name}
            </p>

            <p className="text-xs text-slate-500 mt-1">
              {asset.original_file_name}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-slate-100"
          >
            <X size={22} />
          </button>
        </div>

        <div className="flex-1 min-h-0 bg-slate-100 p-4 overflow-auto">
          {isImage(asset) ? (
            <img
              src={viewUrl}
              alt={
                asset.name ||
                asset.original_file_name
              }
              className="max-w-full max-h-[76vh] mx-auto object-contain"
            />
          ) : isVideo(asset) ? (
            <video
              src={viewUrl}
              controls
              className="w-full max-h-[76vh]"
            />
          ) : isPdf(asset) ? (
            <iframe
              src={viewUrl}
              title={
                asset.name ||
                asset.original_file_name
              }
              className="w-full h-[76vh] bg-white rounded-lg"
            />
          ) : (
            <div className="h-[55vh] flex flex-col items-center justify-center text-slate-500">
              <FileText size={58} />
              <p className="mt-4">
                Visualização interna indisponível para este formato.
              </p>

              <a
                href={viewUrl}
                target="_blank"
                rel="noreferrer"
                className="mt-4 px-4 py-2 rounded-lg bg-slate-900 text-white font-semibold"
              >
                Abrir arquivo
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Field = ({
  label,
  children,
}) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-2">
      {label}
    </label>

    {children}
  </div>
);

export default EnterpriseAssetsPanel;
